import { Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Section } from '../../grid-system/section/section';
import { ErrorCatcher } from '../../tools/error-catcher';
import { focusPosition } from '../../tools/get-image-focus';
import { getImageServiceSrcPath } from '../../components/image-component/image-component';
import { TestimonyProps } from './testimony-types';
import clsx from 'clsx';
import { QuoteCviIcon } from '../../components/icons-library/quote-cvi';

export const Testimony = (props: TestimonyProps) => {
    const { imagesServiceUrl, supportedImageFormats } = useContext(AppContext);

    const buildImageSrc = (width: number) => {
        if (!props.src) return '';
        return getImageServiceSrcPath({
            imagesServiceUrl,
            supportedImageFormats,
            src: props.src,
            width,
            height: 'AUTO',
            focus: props.focus,
        });
    };

    return (
        <ErrorCatcher componentName="Testimony">
            <div class="bg-cvi-bg-block-default py-cvi-section-default">
                <Section
                    templateShorthand={[12]}
                    widthType="bgFull"
                    removeSpacingBlock={true}
                    customCss="padding-top: 0 !important; padding-bottom: 0 !important; background: transparent;"
                >
                    <div class="flex flex-col">
                        <div class="flex flex-col gap-cvi-4">
                            <Show when={props.eyebrowHeading}>
                                <div class="flex items-center gap-cvi-3">
                                    <span class="size-2.5 rounded-cvi-round bg-cvi-fg-brand shrink-0" aria-hidden="true" />
                                    <span class="body-sm font-cvi-medium text-cvi-fg-default">
                                        {props.eyebrowHeading}
                                    </span>
                                </div>
                            </Show>
                            <Show when={props.testimonyHeading}>
                                <h2 class="heading-3xl font-cvi-medium text-cvi-fg-default m-0">
                                    {props.testimonyHeading}
                                </h2>
                            </Show>
                        </div>

                        <div
                            class={clsx(
                                'pt-cvi-section-close',
                                'grid grid-cols-1 gap-y-cvi-8 items-center',
                                'tablet-original:grid-cols-12 tablet-original:gap-y-0 tablet-original:gap-x-cvi-8',
                            )}
                        >
                            <div class={clsx(
                                'tablet-original:col-span-4',
                                props.imagePosition === 'right' && 'tablet-original:order-2',
                            )}>
                                <Show when={props.src}>
                                    <div class="relative rounded-cvi-md overflow-hidden bg-cvi-bg-neutral-subtle aspect-[21/23]">
                                        <img
                                            src={buildImageSrc(750)}
                                            srcset={[480, 600, 750, 960, 1200, 1500]
                                                .map((w) => `${buildImageSrc(w)} ${w}w`)
                                                .join(', ')}
                                            sizes="(min-width: 768px) 33vw, 100vw"
                                            alt={props.altText ?? ''}
                                            class="size-full object-cover"
                                            style={{
                                                'object-position': props.focus
                                                    ? focusPosition(props.focus)
                                                    : 'center',
                                            }}
                                        />
                                    </div>
                                </Show>
                            </div>

                            <div class="tablet-original:col-span-8 tablet-original:self-stretch desktop:grid desktop:grid-cols-subgrid">
                                <div class={clsx(
                                    'flex flex-col justify-center gap-cvi-10 tablet-original:h-full desktop:col-span-6',
                                    props.imagePosition !== 'right' && 'desktop:col-start-2',
                                )}>
                                    <QuoteCviIcon class="shrink-0 size-cvi-9 text-cvi-fg-brand tablet-original:size-cvi-11" />
                                    <Show when={props.quote?.children}>
                                        <blockquote class="m-0 p-0">
                                            <p class="heading-5xl font-cvi-regular text-cvi-fg-default m-0">
                                                {props.quote!.children}
                                            </p>
                                        </blockquote>
                                    </Show>
                                    <Show when={props.authorName || props.authorShortDescription}>
                                        <div class="flex flex-col">
                                            <Show when={props.authorName}>
                                                <span class="body-md font-cvi-medium text-cvi-fg-default">
                                                    {props.authorName}<span class="text-cvi-fg-brand" aria-hidden="true">_</span>
                                                </span>
                                            </Show>
                                            <Show when={props.authorShortDescription}>
                                                <span class="body-md font-cvi-regular text-cvi-fg-default">
                                                    {props.authorShortDescription}
                                                </span>
                                            </Show>
                                        </div>
                                    </Show>
                                </div>
                            </div>
                        </div>
                    </div>
                </Section>
            </div>
        </ErrorCatcher>
    );
};

Testimony.parseProps = (atts: any) => {
    const { image, quote } = atts;

    const commonAtts = {
        eyebrowHeading: atts.eyebrowHeading,
        testimonyHeading: atts.testimonyHeading,
        authorName: atts.authorName,
        authorShortDescription: atts.authorShortDescription,
        imagePosition: atts.imagePosition,
        quote: quote?.[0],
    };

    if (image) {
        return {
            src: image.url,
            focus: image.focus,
            altText: image?.altText,
            ...commonAtts,
        };
    }
    return commonAtts;
};
